import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/v2`;

const EmailVerificationPage = () => {
  const [verificationStatus, setVerificationStatus] = useState("loading"); // "loading", "success", "error"
  const [featureFlags, setFeatureFlags] = useState({
    FEATURE_EMAIL_VERIFICATION: false,
  });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        const response = await axios.get(`${API}/feature-flags/`);
        setFeatureFlags(response.data);
      } catch (error) {
        console.error("Failed to fetch feature flags:", error);
        setFeatureFlags({ FEATURE_EMAIL_VERIFICATION: false }); // Default to false on error
      }
    };
    fetchFeatureFlags();
  }, []);

  useEffect(() => {
    if (!featureFlags.FEATURE_EMAIL_VERIFICATION) {
      setVerificationStatus("error"); // Or redirect to a different page
      return;
    }

    const verifyEmail = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");

      if (!token) {
        setVerificationStatus("error");
        return;
      }

      try {
        await axios.get(`${API}/auth/verify-email/?token=${token}`);
        setVerificationStatus("success");
      } catch (error) {
        console.error("Email verification failed:", error);
        setVerificationStatus("error");
      }
    };

    if (featureFlags.FEATURE_EMAIL_VERIFICATION !== false) { // Ensure flags are loaded before proceeding
      verifyEmail();
    }
  }, [location.search, featureFlags.FEATURE_EMAIL_VERIFICATION]);

  const handleGoToLogin = () => {
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-200 shadow-xl">
        <CardHeader className="text-center">
          {verificationStatus === "loading" && (
            <Loader2 className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
          )}
          {verificationStatus === "success" && (
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          )}
          {verificationStatus === "error" && (
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          )}
          <CardTitle className="text-2xl">
            {verificationStatus === "loading" && "Verifying Email..."}
            {verificationStatus === "success" && "Email Verified!"}
            {verificationStatus === "error" && "Verification Failed"}
          </CardTitle>
          <CardDescription>
            {verificationStatus === "loading" && "Please wait while we verify your email address."}
            {verificationStatus === "success" && "Your email address has been successfully verified. You can now log in to your account."}
            {verificationStatus === "error" && "There was an issue verifying your email address. The link might be invalid or expired."}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          {verificationStatus !== "loading" && (
            <Button onClick={handleGoToLogin} className="w-full">
              Go to Login
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailVerificationPage;
